import { DocumentUserPermission } from '@quadminds/client-apis/agents';
import { toast } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGePermissions from './useGetAllPermissions';
import useMapPermissions from './useMapPermissions';
import { useOrganizationUsers } from './useOrganizationUsers';
import useSetPermissions from './useSetPermissions';
import { FileItem } from '../types/File/File.types';
import { hasAdminRole } from '../utils/utils';

interface UserRole {
    id: string;
    name: string;
}

interface PermissionMap {
    [key: string]: string;
}

const usePermissions = (file: FileItem, isOpen: boolean, onClose: () => void, ownerAvatar?: string) => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [suggestions, setSuggestions] = useState<DocumentUserPermission[]>([]);
    const { getAllPermissionsQuery, getPermissionsPerDocument } = useGePermissions(isOpen, file.id);
    const [selectedUsers, setSelectedUsers] = useState<DocumentUserPermission[]>([]);
    const [permissionsToDelete, setPermissionsToDelete] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

    const [originalPermissions, setOriginalPermissions] = useState<DocumentUserPermission[]>([]);
    const [modifiedPermissions, setModifiedPermissions] = useState<Set<string>>(new Set());

    const suggestionsRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { setPermissionsMutation } = useSetPermissions(onClose);
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const { data: organizationUsers } = useOrganizationUsers(userInfo?.currentOrganization ?? 'a', 1, 1000);
    const { isAdmin, isEditor, isViewer } = useMapPermissions(file);
    const mindyAdmin = useMemo(() => hasAdminRole(userInfo?.roles), [userInfo]);

    const isLoading = useMemo(() => {
        return getPermissionsPerDocument.isLoading || setPermissionsMutation.isPending;
    }, [getPermissionsPerDocument.isLoading, setPermissionsMutation.isPending]);

    const generalPermission = useMemo(() => {
        return selectedUsers.find((user) => user.user_id === null) || null;
    }, [selectedUsers]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSearchValue(value);

        if (value.trim()) {
            const mappedUsers: DocumentUserPermission[] = (organizationUsers?.data ?? []).map((user) => ({
                user_id: user.id,
                user_name: user.firstName + ' ' + user.lastName,
                user_email: user.email,
                organization_id: '',
                user_photo: user.avatar ?? null,
                permissions: [''],
                roles:
                    user.userOrganizations
                        ?.find((org) => org.organization === userInfo?.currentOrganization)
                        ?.roles.filter((role) => typeof role.id === 'string')
                        .map((role) => ({ id: role.id as string, name: role.name })) || [],
            }));
            const filtered = mappedUsers.filter(
                (user) =>
                    !selectedUsers.some((selected) => selected.user_id === user.user_id) &&
                    ((user.user_name?.toLowerCase() ?? '').includes(value.toLowerCase()) ||
                        (user.user_email?.toLowerCase() ?? '').includes(value.toLowerCase())),
            );
            setSuggestions(filtered);
            setShowSuggestions(true);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const handleSelectUser = (user: DocumentUserPermission) => {
        const viewerId = getAllPermissionsQuery.data?.find((permission) => permission.code === 'viewer')?.id;

        const userWithRole = {
            ...user,
            permissions: viewerId ? generateArrayPermissions([viewerId]) : [],
        };

        setSelectedUsers((prevUsers) => {
            const newUsers = [...prevUsers, userWithRole];
            return sortUsersByRole(newUsers);
        });

        setModifiedPermissions((prev) => new Set([...prev, user.user_id || 'general']));

        setSearchValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleRemoveUser = (userId?: string | null) => {
        if (userId === undefined) return;

        if (file.uploaded_by?.userId === userId) {
            return;
        }

        const userToDelete = selectedUsers.find((userPermission) => userPermission.user_id === userId);

        setSelectedUsers(selectedUsers.filter((permission) => permission.user_id !== userId));

        if (userToDelete?.id) {
            setPermissionsToDelete([...permissionsToDelete, userToDelete.id]);
        }

        setModifiedPermissions((prev) => {
            const newSet = new Set(prev);
            newSet.delete(userId || 'general');
            return newSet;
        });
    };

    const handleRoleChange = (userId: string | null, newRole: UserRole) => {
        const userKey = userId || 'general';

        if (userId) {
            setSelectedUsers((prevUsers) => {
                const updatedUsers = prevUsers.map((user) =>
                    user.user_id === userId ? { ...user, permissions: generateArrayPermissions([newRole.id]) } : user,
                );
                return sortUsersByRole(updatedUsers);
            });
        } else {
            if (newRole.id === 'none') {
                const generalUser = selectedUsers.find((u) => u.user_id === null);
                if (generalUser?.id) {
                    setPermissionsToDelete([...permissionsToDelete, generalUser.id]);
                }

                setSelectedUsers((prevUsers) => {
                    const usersWithoutGeneral = prevUsers.filter((user) => user.user_id !== null);
                    return sortUsersByRole(usersWithoutGeneral);
                });

                setModifiedPermissions((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete('general');
                    return newSet;
                });
                return;
            }

            const newGeneralPermission: DocumentUserPermission = {
                id: selectedUsers.find((u) => u.user_id === null)?.id,
                user_id: null,
                user_name: null,
                user_email: null,
                organization_id: '',
                user_photo: null,
                permissions: generateArrayPermissions([newRole.id]),
            };

            setSelectedUsers((prevUsers) => {
                const usersWithoutGeneral = prevUsers.filter((user) => user.user_id !== null);
                const updatedUsers = [...usersWithoutGeneral, newGeneralPermission];
                return sortUsersByRole(updatedUsers);
            });
        }

        const originalUser = originalPermissions.find((u) => (userId ? u.user_id === userId : u.user_id === null));

        const newPermissions = generateArrayPermissions([newRole.id]);

        const originalPermissionsSorted = originalUser
            ? [...originalUser.permissions].sort((a, b) => a.localeCompare(b))
            : [];
        const newPermissionsSorted = [...newPermissions].sort((a, b) => a.localeCompare(b));

        const hasChanged =
            !originalUser || JSON.stringify(originalPermissionsSorted) !== JSON.stringify(newPermissionsSorted);

        if (hasChanged) {
            setModifiedPermissions((prev) => new Set([...prev, userKey]));
        } else {
            setModifiedPermissions((prev) => {
                const newSet = new Set(prev);
                newSet.delete(userKey);
                return newSet;
            });
        }
    };

    const sortUsersByRole = useCallback(
        (users: DocumentUserPermission[]): DocumentUserPermission[] => {
            if (!file.uploaded_by?.userId) return users;

            return users.sort((a, b) => {
                const aIsOwner = a.user_id === file.uploaded_by?.userId;
                const bIsOwner = b.user_id === file.uploaded_by?.userId;

                if (aIsOwner && !bIsOwner) return -1;
                if (bIsOwner && !aIsOwner) return 1;
                return 0;
            });
        },
        [file.uploaded_by?.userId],
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const permissions = getPermissionsPerDocument.data ?? [];

        setOriginalPermissions([...permissions]);

        const generalPermissionFromAPI = permissions.find((permission) => permission.user_id === null);
        const userPermissions = permissions.filter((permission) => permission.user_id !== null);

        if (userPermissions.length === 0 && file.uploaded_by?.userId) {
            const adminId = getAllPermissionsQuery.data?.find((permission) => permission.code === 'admin')?.id;

            if (adminId) {
                const ownerPermission: DocumentUserPermission = {
                    user_id: file.uploaded_by.userId,
                    user_name: file.uploaded_by.fullName,
                    user_email: '',
                    organization_id: '',
                    user_photo: ownerAvatar || null,
                    permissions: isAdmin ? [adminId] : [],
                };

                const allUsers = generalPermissionFromAPI
                    ? [ownerPermission, generalPermissionFromAPI]
                    : [ownerPermission];

                setSelectedUsers(allUsers);
            }
        } else {
            const allUsers = generalPermissionFromAPI
                ? [...userPermissions, generalPermissionFromAPI]
                : userPermissions;

            const updatedUsers = allUsers.map((user) => {
                if (user.user_id === file.uploaded_by?.userId) {
                    return {
                        ...user,
                        user_photo: ownerAvatar || user.user_photo,
                    };
                }
                return user;
            });
            const sortedUsers = sortUsersByRole(updatedUsers);
            setSelectedUsers(sortedUsers);
        }

        setModifiedPermissions(new Set());
    }, [
        getPermissionsPerDocument.data,
        file.uploaded_by,
        getAllPermissionsQuery.data,
        userInfo?.currentOrganization,
        isAdmin,
        sortUsersByRole,
        ownerAvatar,
    ]);

    if (getAllPermissionsQuery.error) {
        toast({
            title: 'Error',
            description: 'No se pudieron cargar los permisos.',
            variant: 'destructive',
        });
    }

    const handleUpdatePermissions = () => {
        const permissionsToAdd = selectedUsers
            .filter((user) => {
                const userKey = user.user_id || 'general';
                return modifiedPermissions.has(userKey) || !user.id;
            })
            .map((user) => {
                return {
                    folder_id: file.parentId ?? '',
                    document_id: file.id,
                    user: user.user_id
                        ? {
                              id: user.user_id,
                              name: user.user_name ?? '',
                              email: user.user_email ?? '',
                              photo: user.user_photo ?? undefined,
                          }
                        : null,
                    permissions: user.permissions,
                };
            });

        setPermissionsMutation.mutate({
            permissions_to_add: permissionsToAdd,
            permission_ids_to_delete: permissionsToDelete,
            document_id: file.id,
        });
    };

    const generateArrayPermissions = (userPermissions: string[]): string[] => {
        if (!getAllPermissionsQuery.data) return [];

        const permissionMap: PermissionMap = Object.fromEntries(
            getAllPermissionsQuery.data.map((perm: { code: string; id: string }) => [perm.code, perm.id]),
        );

        const adminId = permissionMap['admin'];
        const editorId = permissionMap['editor'];
        const viewerId = permissionMap['viewer'];
        const consultorId = permissionMap['consultant'];

        const selectedPermissionId = userPermissions[0];

        if (selectedPermissionId === adminId) {
            return [adminId, editorId, viewerId, consultorId].filter(Boolean);
        }

        if (selectedPermissionId === editorId) {
            return [editorId, viewerId, consultorId].filter(Boolean);
        }

        if (selectedPermissionId === viewerId) {
            return [viewerId, consultorId].filter(Boolean);
        }

        if (selectedPermissionId === consultorId) {
            return [consultorId].filter(Boolean);
        }

        return userPermissions;
    };

    const arrayPermissionToOne = useCallback(
        (permissions: string[]): string | null => {
            if (!getAllPermissionsQuery.data || !permissions.length) return null;

            const permissionMap: PermissionMap = Object.fromEntries(
                getAllPermissionsQuery.data.map((perm: { code: string; id: string }) => [perm.code, perm.id]),
            );

            const adminId = permissionMap['admin'];
            const editorId = permissionMap['editor'];
            const viewerId = permissionMap['viewer'];
            const consultorId = permissionMap['consultant'];

            if (permissions.includes(adminId)) {
                return adminId;
            }

            if (permissions.includes(editorId)) {
                return editorId;
            }

            if (permissions.includes(viewerId)) {
                return viewerId;
            }

            if (permissions.includes(consultorId)) {
                return consultorId;
            }

            return permissions[0] || null;
        },
        [getAllPermissionsQuery.data],
    );

    return {
        searchValue,
        setSearchValue,
        suggestions,
        selectedUsers,
        permissionsToDelete,
        showSuggestions,
        suggestionsRef,
        inputRef,
        getAllPermissionsQuery,
        handleChange,
        handleSelectUser,
        handleRemoveUser,
        handleRoleChange,
        handleUpdatePermissions,
        setPermissionsToDelete,
        setShowSuggestions,
        getPermissionsPerDocument,
        setPermissionsMutation,
        userInfo,
        isAdmin,
        isEditor,
        isViewer,
        generalPermission,
        arrayPermissionToOne,
        isLoading,
        mindyAdmin,
        organizationUsers,
    };
};

export default usePermissions;
